(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-interactive-cleaning-world]';
  const TABLET_BREAKPOINT = 1024;

  class InteractiveCleaningWorld {
    constructor(root) {
      if (!root || root.dataset.icwInitialized === 'true') return;

      this.root = root;
      this.root.dataset.icwInitialized = 'true';

      this.visual = root.querySelector('[data-icw-visual]');
      this.panel = root.querySelector('[data-icw-panel]');
      this.panelContent = root.querySelector('[data-icw-panel-content]');
      this.closeButton = root.querySelector('[data-icw-close]');
      this.dataElement = root.querySelector('[data-icw-data]');
      this.hotspots = Array.from(root.querySelectorAll('[data-icw-hotspot]'));
      this.mobileTabs = Array.from(root.querySelectorAll('[data-icw-activate]'));

      this.activeKey = null;
      this.resizeFrame = null;
      this.destroyed = false;
      this.listeners = [];
      this.observer = null;

      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pointerFine = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.collectionData = this.parseData();

      if (!this.visual || !this.panel || !this.panelContent) return;

      this.bindEvents();
      this.setupInitialAnimation();
    }

    parseData() {
      if (!this.dataElement) return {};

      try {
        return JSON.parse(this.dataElement.textContent || '{}');
      } catch (error) {
        console.warn('Interactive Cleaning World: invalid collection data.', error);
        return {};
      }
    }

    addListener(target, event, handler, options) {
      if (!target) return;
      target.addEventListener(event, handler, options);
      this.listeners.push(() => target.removeEventListener(event, handler, options));
    }

    bindEvents() {
      this.hotspots.forEach((hotspot, index) => {
        const key = hotspot.dataset.icwHotspot;

        const onMouseEnter = () => {
          if (this.isDesktopHover()) this.activate(key, hotspot, false);
        };

        const onClick = (event) => {
          event.preventDefault();
          this.activate(key, hotspot, true);
        };

        const onFocus = () => {
          this.activate(key, hotspot, false);
        };

        const onKeyDown = (event) => {
          this.handleHotspotKeyboard(event, index);
        };

        this.addListener(hotspot, 'mouseenter', onMouseEnter);
        this.addListener(hotspot, 'click', onClick);
        this.addListener(hotspot, 'focus', onFocus);
        this.addListener(hotspot, 'keydown', onKeyDown);
      });

      this.mobileTabs.forEach((tab) => {
        const onClick = () => {
          const key = tab.dataset.icwActivate;
          const hotspot = this.findHotspot(key);
          this.activate(key, hotspot, true);
        };

        this.addListener(tab, 'click', onClick);
      });

      this.addListener(this.closeButton, 'click', () => this.deactivate());

      this.addListener(this.root, 'keydown', (event) => {
        if (event.key === 'Escape' && this.activeKey) {
          this.deactivate(true);
        }
      });

      this.addListener(window, 'resize', () => {
        if (!this.activeKey) return;

        if (this.resizeFrame) {
          window.cancelAnimationFrame(this.resizeFrame);
        }

        this.resizeFrame = window.requestAnimationFrame(() => {
          const activeHotspot = this.findHotspot(this.activeKey);
          this.positionPanel(activeHotspot);
          this.resizeFrame = null;
        });
      }, { passive: true });

      if (window.Shopify && window.Shopify.designMode) {
        this.addListener(document, 'shopify:section:unload', (event) => {
          if (event.target && event.target.contains(this.root)) {
            this.destroy();
          }
        });
      }
    }

    isDesktopHover() {
      return window.innerWidth > TABLET_BREAKPOINT && this.pointerFine.matches;
    }

    findHotspot(key) {
      return this.hotspots.find((item) => item.dataset.icwHotspot === key) || null;
    }

    handleHotspotKeyboard(event, index) {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      event.preventDefault();

      let nextIndex = index;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (index + 1) % this.hotspots.length;
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (index - 1 + this.hotspots.length) % this.hotspots.length;
      }

      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.hotspots.length - 1;

      this.hotspots[nextIndex]?.focus();
    }

    activate(key, hotspot, userInitiated) {
      const data = this.collectionData[key];

      if (!data || !data.enabled) return;

      this.activeKey = key;
      this.root.classList.add('is-active');

      this.hotspots.forEach((item) => {
        const active = item.dataset.icwHotspot === key;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-expanded', active ? 'true' : 'false');
      });

      this.mobileTabs.forEach((tab) => {
        const active = tab.dataset.icwActivate === key;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.renderPanel(data);
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');

      this.positionPanel(hotspot);

      if (
        userInitiated &&
        window.innerWidth <= TABLET_BREAKPOINT &&
        !this.prefersReducedMotion.matches
      ) {
        window.requestAnimationFrame(() => {
          const panelRect = this.panel.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

          if (panelRect.bottom > viewportHeight + 24) {
            this.panel.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          }
        });
      }
    }

    deactivate(returnFocus = false) {
      const previousHotspot = this.activeKey ? this.findHotspot(this.activeKey) : null;

      this.activeKey = null;
      this.root.classList.remove('is-active');

      this.hotspots.forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-expanded', 'false');
      });

      this.mobileTabs.forEach((tab) => {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-pressed', 'false');
      });

      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');

      if (returnFocus && previousHotspot) {
        previousHotspot.focus();
      }
    }

    renderPanel(data) {
      const products = Array.isArray(data.products)
        ? data.products.slice(0, 3)
        : [];

      const productMarkup = products.length
        ? `
          <div class="interactive-cleaning-world__products" aria-label="Featured products">
            ${products.map((product) => this.productTemplate(product)).join('')}
          </div>
        `
        : '';

      const descriptionMarkup = data.description
        ? `<p class="interactive-cleaning-world__panel-description">${this.escapeHTML(data.description)}</p>`
        : '';

      const ctaMarkup = data.url
        ? `
          <a
            class="interactive-cleaning-world__panel-cta"
            href="${this.escapeAttribute(data.url)}"
          >
            ${this.escapeHTML(data.cta || `Explore ${data.title || 'collection'}`)}
          </a>
        `
        : '';

      this.panelContent.innerHTML = `
        <p class="interactive-cleaning-world__panel-kicker">Explore our solutions</p>
        <h3 class="interactive-cleaning-world__panel-title">${this.escapeHTML(data.title || '')}</h3>
        ${descriptionMarkup}
        ${productMarkup}
        ${ctaMarkup}
      `;
    }

    productTemplate(product) {
      const title = this.escapeHTML(product.title || '');
      const url = this.escapeAttribute(product.url || '#');
      const price = this.escapeHTML(product.price || '');
      const image = this.escapeAttribute(product.image || '');
      const imageAlt = this.escapeAttribute(product.imageAlt || product.title || '');

      const imageMarkup = image
        ? `
          <img
            class="interactive-cleaning-world__product-image"
            src="${image}"
            alt="${imageAlt}"
            loading="lazy"
            width="520"
            height="520"
          >
        `
        : `<span class="interactive-cleaning-world__product-placeholder" aria-hidden="true"></span>`;

      return `
        <article class="interactive-cleaning-world__product">
          <a class="interactive-cleaning-world__product-link" href="${url}">
            <span class="interactive-cleaning-world__product-media">
              ${imageMarkup}
            </span>
            <span class="interactive-cleaning-world__product-name">${title}</span>
            ${price ? `<span class="interactive-cleaning-world__product-price">${price}</span>` : ''}
          </a>
        </article>
      `;
    }

    positionPanel(hotspot) {
      if (!hotspot || window.innerWidth <= TABLET_BREAKPOINT) {
        this.panel.style.left = '';
        this.panel.style.top = '';
        return;
      }

      const visualRect = this.visual.getBoundingClientRect();
      const hotspotRect = hotspot.getBoundingClientRect();

      const panelWidth = this.panel.offsetWidth || 430;
      const panelHeight = this.panel.offsetHeight || 360;

      const gap = 20;
      const safe = 16;

      const hotspotCenterX = hotspotRect.left + hotspotRect.width / 2;
      const hotspotCenterY = hotspotRect.top + hotspotRect.height / 2;
      const visualCenterX = visualRect.left + visualRect.width / 2;

      let viewportLeft;

      if (hotspotCenterX >= visualCenterX) {
        viewportLeft = hotspotRect.left - panelWidth - gap;
      } else {
        viewportLeft = hotspotRect.right + gap;
      }

      const minLeft = visualRect.left + safe;
      const maxLeft = visualRect.right - panelWidth - safe;

      if (viewportLeft < minLeft || viewportLeft > maxLeft) {
        const leftOption = visualRect.left + safe;
        const rightOption = visualRect.right - panelWidth - safe;

        viewportLeft =
          Math.abs(hotspotCenterX - leftOption) >
          Math.abs(hotspotCenterX - (rightOption + panelWidth))
            ? leftOption
            : rightOption;
      }

      viewportLeft = Math.max(minLeft, Math.min(viewportLeft, maxLeft));

      let viewportTop = hotspotCenterY - panelHeight / 2;
      const minTop = visualRect.top + safe;
      const maxTop = visualRect.bottom - panelHeight - safe;

      viewportTop = Math.max(minTop, Math.min(viewportTop, maxTop));

      this.panel.style.left = `${viewportLeft - visualRect.left}px`;
      this.panel.style.top = `${viewportTop - visualRect.top}px`;
    }

    setupInitialAnimation() {
      const enabled = this.root.dataset.initialAnimation === 'true';

      if (!enabled || this.prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
        this.root.classList.add('is-in-view');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (entry && entry.isIntersecting) {
            this.root.classList.add('is-in-view');
            this.observer?.disconnect();
            this.observer = null;
          }
        },
        {
          threshold: 0.2
        }
      );

      this.observer.observe(this.root);
    }

    escapeHTML(value) {
      const string = String(value ?? '');
      return string.replace(/[&<>"']/g, (char) => {
        const map = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;'
        };
        return map[char];
      });
    }

    escapeAttribute(value) {
      return this.escapeHTML(value);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      this.listeners.forEach((remove) => remove());
      this.listeners = [];

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.root.dataset.icwInitialized = 'false';
    }
  }

  const instances = new WeakMap();

  const initSection = (root) => {
    if (!root || instances.has(root)) return;
    const instance = new InteractiveCleaningWorld(root);
    instances.set(root, instance);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll(document),
      { once: true }
    );
  } else {
    initAll(document);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.matches?.(ROOT_SELECTOR)) {
        initSection(event.target);
      } else {
        initAll(event.target);
      }
    });
  }
})();
